import { Form, FormInstance, Select } from 'antd'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { parseDate } from '../../../config/dayjsConfig'
import {
  DeliverySubscription,
  ManualDelivery,
  MonthlyDelivery
} from '../../../types/deliverySubscription/DeliverySubscription'
import { transformFieldData } from '../../../utils/form'
import CurrentUserCompaniesSelect from '../../../components/GlobalHeader/CurrentUserCompaniesSelect'
import useCustomReport from '../../customReport/queries/report/useCustomReport'
import { DeliverySubscriptionFormData, InitiatorType } from '../types'
import InitiatorFields from './InitiatorFields'

const INITIATOR_OPTIONS: InitiatorType[] = ['monthly', 'manual']

interface KiinteistomaailmaSubscriptionFormProps {
  form: FormInstance<DeliverySubscriptionFormData>
  deliverySubscription?: DeliverySubscription
}

const KiinteistomaailmaSubscriptionForm: React.FC<KiinteistomaailmaSubscriptionFormProps> = ({
  form,
  deliverySubscription
}) => {
  const { t } = useTranslation()
  const [selectedTargetCompany, setSelectedTargetCompany] = useState<string>()
  const KIINTEISTOMAAILMA_COMPANY_ID = process.env.REACT_APP_KIINTEISTOMAAILMA_COMPANY_ID
  const KIINTEISTOMAAILMA_REPORT_ID = Number(process.env.REACT_APP_KIINTEISTOMAAILMA_REPORT_ID)
  const { data: customReport } = useCustomReport(KIINTEISTOMAAILMA_REPORT_ID, KIINTEISTOMAAILMA_COMPANY_ID)

  const handleTargetCompanyChange = (companyId: string) => {
    setSelectedTargetCompany(companyId)
    if (companyId && customReport?.args) {
      const companyArg = customReport.args.find(arg => arg.type === 'company')
      if (companyArg) {
        form.setFieldValue(
          ['params', 'args'],
          [
            {
              id: companyArg.id,
              type: 'company',
              name: companyArg.name,
              order: companyArg.order || null,
              reportId: KIINTEISTOMAAILMA_REPORT_ID,
              values: [{ id: companyId }]
            }
          ]
        )
      }
    } else {
      form.setFieldValue(['params', 'args'], [])
    }
  }

  useEffect(() => {
    form.resetFields()
    form.setFieldValue('companyId', KIINTEISTOMAAILMA_COMPANY_ID)
    form.setFieldValue('reportId', KIINTEISTOMAAILMA_REPORT_ID)

    if (deliverySubscription) {
      if (((deliverySubscription.initiator as MonthlyDelivery).monthly as { day: number })?.day) {
        form.setFieldValue(['initiator', 'type'], 'monthly')
      }
      if ((deliverySubscription.initiator as ManualDelivery).manual) {
        form.setFieldValue(['initiator', 'type'], 'manual')
      }

      const fields = transformFieldData(deliverySubscription, {
        endDate: (value: any) => value && parseDate(value)?.endOf('month'),
        recipients: (value: any) => value || undefined,
        targetCompanies: (value: any) => value || undefined
      })
      form.setFieldsValue(fields)

      if ((deliverySubscription.params?.args?.length ?? 0) > 0) {
        const companyArg = deliverySubscription.params?.args?.find(arg => arg.type === 'company')
        if (companyArg?.values?.[0]?.id) {
          setSelectedTargetCompany(String(companyArg.values[0].id))
        }
      }
    }
  }, [deliverySubscription, form])

  return (
    <Form
      form={form}
      layout="vertical"
      className="delivery-subsricption-form"
      initialValues={{
        companyId: KIINTEISTOMAAILMA_COMPANY_ID,
        reportId: KIINTEISTOMAAILMA_REPORT_ID
      }}
    >
      <Form.Item name="companyId" style={{ display: 'none' }}>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name="reportId" style={{ display: 'none' }}>
        <input type="hidden" />
      </Form.Item>

      <Form.Item label={t('global:company')}>
        <CurrentUserCompaniesSelect value={selectedTargetCompany} onChange={handleTargetCompanyChange} />
      </Form.Item>

      <Form.Item name={['params', 'args']} hidden>
        <div />
      </Form.Item>

      <Form.Item
        label={t('subscriptionsPage:recipients')}
        name="recipients"
        rules={[{ required: true, message: t('global:required-field') }]}
      >
        <Select mode="tags" optionFilterProp="children" />
      </Form.Item>

      <InitiatorFields options={INITIATOR_OPTIONS} />
    </Form>
  )
}

export default KiinteistomaailmaSubscriptionForm
